'use client';

/**
 * Message state management hook.
 *
 * Handles message insertion and deduplication for the AI assistant chat.
 * All state callbacks are pure functions — no mutable side effects —
 * so they are safe under React StrictMode's double-invocation.
 *
 * Dedup strategy: `prev.some(m => m.id === id)` checks the messages
 * array itself. This is idempotent because React invokes the updater
 * twice with the SAME prev under StrictMode; a pure id check over prev
 * yields the same result both times (the first call's output does not
 * feed the second — React discards it).
 */

import { useState, useCallback, useRef } from 'react';
import {
  ComponentType as MsgComponentType,
  MessageRole,
  ToolName,
  type AgentAttachment,
  type ContactSummary,
  type Message,
} from '@/types/chat';
import type { Course, CourseCards } from '@/types/course';
import { mapPersistedResultToMessage } from '@/lib/tool-result-mapper';
import { formatContactSummaryContent } from '@/lib/format-contact-summary';

/** Params for insertHumanAgentMessage — mirrors the wire shape of the
 *  AGENT_MESSAGE server event (snake_case, matching AgentMessageEvent in
 *  aol_schemas.ws_events) so callers can pass the event straight through. */
export interface AgentMessageInsert {
  message_id: string;
  text: string;
  rich_text?: string | null;
  agent_display_name: string;
  attachments?: AgentAttachment[];
}

/** Params for insertContactSummary — mirrors the wire shape of the
 *  CONTACT_SUMMARY server event (Decision #18.1,
 *  2026-07-hubspot-native-handoff.md — ContactSummaryEvent in
 *  aol_schemas.ws_events) so callers can pass the event straight through.
 *
 *  Extends `@/types/chat`'s `ContactSummary` with the one envelope field
 *  that type deliberately drops: `handoff_id`, which THIS callback needs (to
 *  build the `contact-summary-${handoff_id}` dedup id) even though the
 *  resulting `Message.contactSummary` never stores it. Derives from the one
 *  canonical shape instead of redeclaring name/email/phone a third time. */
export type ContactSummaryInsert = ContactSummary & { handoff_id: string };
import { looksLikeLeakedToolArgs } from '@/lib/message-guards';

// ─── Hook ───────────────────────────────────────────────────────

export function useMessageManagement() {
  const [messages, setMessages] = useState<Message[]>([]);

  // Per-item accumulated stream text, keyed by model item_id. Lives OUTSIDE
  // React state so the setMessages updater can SET (not concatenate) — under
  // StrictMode the updater runs twice with the same prev, so a SET is a no-op
  // the second time instead of doubling the text.
  const streamBuffers = useRef<Map<string, string>>(new Map());

  /** Simple string hash for generating fallback IDs */
  const hashString = (s: string): string => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h).toString(36);
  };

  /** Accumulate a streamed token into the message keyed by `itemId`, creating
   *  it on the first delta. The ref mutation runs once (in the WS callback) and
   *  the updater SETs the accumulated value, so a re-invoked updater is a no-op.
   *  Suppresses (and drops) a bubble that resolves to bare leaked tool-args —
   *  the same guard the authoritative history_added path applies. */
  const appendAssistantDelta = useCallback(
    (itemId: string, delta: string, responseId?: string) => {
      if (!itemId || !delta) return;
      const next = (streamBuffers.current.get(itemId) ?? '') + delta;
      streamBuffers.current.set(itemId, next);
      const suppressed = looksLikeLeakedToolArgs(next);

      setMessages((prev) => {
        const i = prev.findIndex((m) => m.id === itemId);
        if (suppressed) {
          if (i === -1) return prev;
          return [...prev.slice(0, i), ...prev.slice(i + 1)];
        }
        if (i === -1) {
          return [
            ...prev,
            {
              role: 'assistant' as const,
              content: next,
              id: itemId,
              streaming: true,
              responseId,
            },
          ];
        }
        const existing = prev[i];
        if (existing.content === next && existing.streaming) return prev;
        const copy = [...prev];
        copy[i] = {
          ...existing,
          content: next,
          streaming: true,
          responseId: existing.responseId ?? responseId,
        };
        return copy;
      });
    },
    []
  );

  /** Drop the stream buffer for ONE item id, without touching `messages` or
   *  any other buffer. Called by the connection driver's HISTORY_UPDATED
   *  reconcile right after an authoritative snapshot replaces a
   *  still-streaming message: without this, a stray late transcript_delta/
   *  text_delta for that id would resurrect the OLD accumulated text via
   *  appendAssistantDelta's buffer, stomping the just-landed authoritative
   *  content on the next token. insertAssistantMessage already guards its
   *  own path the same way (see there) — this exposes the same guard for
   *  callers that reconcile via a raw `setMessages` updater instead. */
  const dropStreamBuffer = useCallback((itemId: string) => {
    streamBuffers.current.delete(itemId);
  }, []);

  /** Clear all in-flight stream state: drop the buffers and flip any
   *  still-streaming message to done. Called at turn end, on reconnect, and on
   *  session change so no caret is ever stranded. Idempotent. */
  const finalizeStreaming = useCallback(() => {
    streamBuffers.current.clear();
    setMessages((prev) => {
      let changed = false;
      const next = prev.map((m) => {
        if (m.streaming) {
          changed = true;
          return { ...m, streaming: false };
        }
        return m;
      });
      return changed ? next : prev;
    });
  }, []);

  /** Apply the backend commentary/answer classification to every message with
   *  the given responseId. `isFinal=false` = commentary (folds into Thinking);
   *  `true` = the final answer (pushes out to the answer). Idempotent. */
  const classifyMessages = useCallback(
    (responseId: string, isFinal: boolean) => {
      if (!responseId) return;
      setMessages((prev) => {
        let changed = false;
        const next = prev.map((m) => {
          if (m.responseId === responseId && m.isFinal !== isFinal) {
            changed = true;
            return { ...m, isFinal };
          }
          return m;
        });
        return changed ? next : prev;
      });
    },
    []
  );

  const insertAssistantMessage = useCallback(
    (itemId: string | undefined, content: string) => {
      const id =
        itemId ||
        `assistant-${hashString(content).slice(0, 8)}-${Date.now().toString(36)}`;

      // Authoritative reconcile: this content is the source of truth. Drop any
      // stream buffer for the id so a stray late delta can't resurrect it.
      streamBuffers.current.delete(id);

      setMessages((prev) => {
        // Upsert by id — idempotent under StrictMode (findIndex on prev, SET the
        // authoritative content). If a streamed bubble already exists for this
        // id, overwrite its content and clear `streaming` (no duplicate).
        const i = prev.findIndex((m) => m.id === id);
        if (i === -1) {
          return [...prev, { role: 'assistant' as const, content, id }];
        }
        const existing = prev[i];
        if (existing.content === content && !existing.streaming) return prev;
        const copy = [...prev];
        copy[i] = { ...existing, content, streaming: false };
        return copy;
      });
    },
    []
  );

  const insertToolResult = useCallback(
    (
      toolName: string,
      payload: unknown,
      itemId?: string,
      /** find_courses only — the searched zip/city from the tool-call args
       *  (tool_end.arguments), attached to the COURSE_BROWSER result so the
       *  panel can title itself with what was ASKED. Never persisted;
       *  restored rows simply omit it. */
      searchedLocation?: string
    ) => {
      const id = itemId || `tool-${toolName}-${Date.now().toString(36)}`;

      setMessages((prev) => {
        // Pure dedup: idempotent under StrictMode double-invocation
        if (prev.some((m) => m.id === id)) return prev;

        let newMsg: Message | null = null;

        // Extract the render payload from the (already MCP-unwrapped) tool output,
        // then hand it to the shared mapper so a LIVE card is identical to a
        // RESTORED one (the backend persists this same extracted payload).
        if (toolName === ToolName.FIND_COURSES) {
          // MCP returns CourseCards: { data: CourseCard[] }
          // Fallback: some SDK paths may deliver the array directly (no wrapper)
          const result = payload as Partial<CourseCards>;
          const courses = Array.isArray(result?.data)
            ? result.data
            : Array.isArray(payload)
              ? (payload as Course[])
              : undefined;

          if (courses?.length) {
            newMsg = mapPersistedResultToMessage(
              MsgComponentType.COURSE_BROWSER,
              courses,
              id
            );
            if (
              searchedLocation &&
              newMsg?.result?.kind === MsgComponentType.COURSE_BROWSER
            ) {
              newMsg = {
                ...newMsg,
                result: { ...newMsg.result, searchedLocation },
              };
            }
          } else {
            // Live-only: preserve the server message for empty results
            // (e.g., "No courses found near…"). Empty results are not persisted,
            // so this branch never runs on restore.
            const p = payload as Record<string, unknown>;
            const fallbackText =
              typeof p?.message === 'string' ? p.message : null;
            if (fallbackText) {
              newMsg = { role: 'assistant', content: fallbackText, id };
            }
          }
        } else if (
          toolName === ToolName.BROWSE_AOL_CONTENT ||
          toolName === ToolName.FETCH_QUICK_AOL_CONTENT ||
          toolName === ToolName.SEARCH_AOL_CONTENT
        ) {
          // fetch_quick shares browse's translated RAGData envelope (backend
          // _translate_quick_fetch_output), so both render as RAG_CONTENT cards.
          // search_aol_content is the unified tool: the backend translator
          // (_translate_search_aol_output) emits the SAME RAGData envelope keyed
          // on result_kind (brief/answer/facts/safety → summary+references;
          // links → references; hits → empty), so it renders as a RAG_CONTENT
          // card too. No new card shape.
          newMsg = mapPersistedResultToMessage(
            MsgComponentType.RAG_CONTENT,
            payload,
            id
          );
        } else {
          console.warn(
            `[insertToolResult] No handler for tool "${toolName}". ` +
              'Add a case in useMessageManagement.ts and a renderer in tool-registrations.ts.'
          );
        }

        if (!newMsg) {
          // Observable failure — log what arrived so silent drops are debuggable
          console.warn(
            `[insertToolResult] Could not create message for tool "${toolName}".`,
            {
              payloadType: typeof payload,
              payloadKeys:
                payload && typeof payload === 'object'
                  ? Object.keys(payload)
                  : undefined,
              payloadPreview: JSON.stringify(payload)?.slice(0, 200),
            }
          );
          return prev;
        }

        return [...prev, newMsg];
      });
    },
    []
  );

  /** Insert a HUMAN_AGENT message (a HubSpot rep's reply, relayed live over
   *  the WS). Dedup on HubSpot's `message_id` — same pure `prev.some(...)`
   *  pattern as insertAssistantMessage/insertToolResult above, idempotent
   *  under StrictMode double-invocation AND safe against a webhook
   *  redelivery resending the same message. */
  const insertHumanAgentMessage = useCallback((event: AgentMessageInsert) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === event.message_id)) return prev;
      return [
        ...prev,
        {
          role: MessageRole.HUMAN_AGENT,
          content: event.text,
          richText: event.rich_text ?? undefined,
          agentDisplayName: event.agent_display_name,
          attachments: event.attachments,
          id: event.message_id,
        },
      ];
    });
  }, []);

  /** Insert the post-submit "Details shared" summary card (Decision #18.1,
   *  2026-07-hubspot-native-handoff.md) on the live CONTACT_SUMMARY event.
   *  Keyed by `handoff_id` (not a fresh id per call) — a handoff has at most
   *  one contact submission, so this doubling as the dedup key means a
   *  redelivered/duplicate event can never insert the card twice, same
   *  pure `prev.some(...)` pattern as the other insert callbacks. Renders as
   *  a SYSTEM-role message; `content` is reconstructed to match the
   *  backend's persisted format exactly (see format-contact-summary.ts) so
   *  the live-rendered and restored-after-reload copy are identical. */
  const insertContactSummary = useCallback((event: ContactSummaryInsert) => {
    const id = `contact-summary-${event.handoff_id}`;
    setMessages((prev) => {
      if (prev.some((m) => m.id === id)) return prev;
      return [
        ...prev,
        {
          role: MessageRole.SYSTEM,
          content: formatContactSummaryContent(event),
          contactSummary: {
            name: event.name,
            email: event.email,
            phone: event.phone ?? undefined,
          },
          id,
        },
      ];
    });
  }, []);

  /** Stamp the server-minted `messages.id` onto the live message keyed by
   *  `item_id`, once the backend's `message_persisted` frame confirms the
   *  row is durably written (see useAssistantConnection.ts's dispatch of
   *  WSEventType.MESSAGE_PERSISTED). This is the id-bridge fix for
   *  per-message feedback on LIVE answers: before this frame existed, a
   *  thumb on a live bubble tried to submit the OpenAI Realtime item_id
   *  where `feedback.message_id` (a real FK to `messages.id`) is required,
   *  and the backend 422'd because that id was never a UUID it minted —
   *  see Message.dbId's doc comment in types/chat.ts for the full picture.
   *
   *  A frame whose item_id matches no live message is a safe no-op (logged,
   *  not thrown): the message could already be gone (session change / new
   *  chat) by the time this arrives, and a cosmetic feedback affordance is
   *  never worth crashing the session over. Never inserts a placeholder row
   *  for an unmatched id — there is nothing to attach the dbId to. */
  const setMessageDbId = useCallback((itemId: string, dbId: string) => {
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.id === itemId);
      if (i === -1) {
        console.debug(
          `[setMessageDbId] no live message for item_id "${itemId}" — dropping message_persisted frame.`
        );
        return prev;
      }
      if (prev[i].dbId === dbId) return prev;
      const copy = [...prev];
      copy[i] = { ...prev[i], dbId };
      return copy;
    });
  }, []);

  return {
    messages,
    setMessages,
    insertAssistantMessage,
    insertToolResult,
    insertHumanAgentMessage,
    insertContactSummary,
    appendAssistantDelta,
    dropStreamBuffer,
    finalizeStreaming,
    classifyMessages,
    setMessageDbId,
  };
}
